import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiCheck, FiLoader, FiUserPlus } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../lib/supabase';
import { getFriends } from '../services/friendService';
import { inviteUsersToRound } from '../services/roundService';
import { useToast } from '../contexts/ToastContext';
import { getInitials } from '../utils/initials';

/**
 * Modal for inviting friends / group-members to an existing round.
 *
 * Pulls addressable users from getFriends() and (for group rounds) the round's
 * `group_members` rows, de-dupes, and filters out the round creator and anyone
 * already in `round.invitations`. Multi-select; submits via
 * `inviteUsersToRound`. The DB trigger handles notifications.
 */
function InvitePlayersModal({ isOpen, onClose, round, onInviteSuccess }) {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !round) return;

    let cancelled = false;
    setLoading(true);
    setSelected(new Set());
    setCandidates([]);

    const load = async () => {
      const tasks = [getFriends()];
      if (round.group_id) {
        tasks.push(
          supabase
            .from('group_members')
            .select('user_id, member_name')
            .eq('group_id', round.group_id),
        );
      }

      const results = await Promise.all(tasks);
      if (cancelled) return;

      const friendRows = results[0]?.data || [];
      const groupRows = round.group_id ? results[1]?.data || [] : [];

      // Build a single map keyed by user_id. Prefer the group_member's
      // `member_name` (more local context) over the friend's display name.
      const map = new Map();
      friendRows.forEach((f) => {
        const id = f.friend_id || f.user_id || f.id;
        if (!id) return;
        const name = f.full_name || f.display_name || f.name || f.email || 'Friend';
        map.set(id, { user_id: id, name });
      });
      groupRows.forEach((m) => {
        if (!m.user_id) return;
        const existing = map.get(m.user_id);
        const name = m.member_name || existing?.name || 'Member';
        map.set(m.user_id, { user_id: m.user_id, name });
      });

      const invitedIds = new Set((round.invitations || []).map((inv) => inv.user_id));
      const creatorId = round.created_by;

      const filtered = [...map.values()]
        .filter((u) => u.user_id !== creatorId && !invitedIds.has(u.user_id))
        .sort((a, b) => a.name.localeCompare(b.name));

      setCandidates(filtered);
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [isOpen, round]);

  if (!isOpen) return null;

  const toggle = (userId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (selected.size === 0 || submitting) return;
    setSubmitting(true);
    const { error } = await inviteUsersToRound(round.id, [...selected]);
    setSubmitting(false);
    if (error) {
      toast?.error?.('Could not send invitations. Please try again.');
      return;
    }
    toast?.success?.(
      selected.size === 1 ? 'Invitation sent' : `${selected.size} invitations sent`,
    );
    onInviteSuccess?.();
  };

  const handleClose = () => {
    if (submitting) return;
    onClose?.();
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[60]"
        onClick={handleClose}
      >
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="bg-card rounded-t-lg sm:rounded-lg shadow-modal max-w-md w-full mb-20 sm:mb-0 flex flex-col max-h-[80vh]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="p-5 border-b border-border-subtle flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold text-text-primary">Invite players</h2>
              <p className="text-xs text-text-muted mt-0.5">
                Pick friends or group members to invite.
              </p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              aria-label="Close"
              className="w-8 h-8 bg-surface rounded-lg flex items-center justify-center hover:bg-border-subtle transition-colors disabled:opacity-50"
            >
              <SafeIcon icon={FiX} className="w-4 h-4 text-text-secondary" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {loading ? (
              <div className="space-y-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-14 rounded-lg bg-surface animate-pulse"
                  />
                ))}
              </div>
            ) : candidates.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 rounded-full bg-accent/10 flex items-center justify-center mx-auto mb-3">
                  <SafeIcon icon={FiUserPlus} className="w-5 h-5 text-accent" />
                </div>
                <p className="text-sm text-text-secondary">
                  No friends or group members left to invite.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {candidates.map((u) => {
                  const isSelected = selected.has(u.user_id);
                  return (
                    <button
                      key={u.user_id}
                      type="button"
                      onClick={() => toggle(u.user_id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-colors text-left ${
                        isSelected
                          ? 'border-accent bg-accent/5'
                          : 'border-border-subtle bg-card hover:border-accent/50'
                      }`}
                      aria-pressed={isSelected}
                    >
                      <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                        <span className="font-semibold text-sm text-accent">
                          {getInitials(u.name)}
                        </span>
                      </div>
                      <span className="flex-1 font-medium text-text-primary truncate">
                        {u.name}
                      </span>
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? 'bg-accent border-accent'
                            : 'border-text-muted'
                        }`}
                      >
                        {isSelected && (
                          <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="p-4 border-t border-border-subtle flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 gap-2 sm:gap-0">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2.5 rounded-lg border border-border-subtle text-text-secondary hover:bg-surface transition-colors text-sm font-semibold disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={submitting || selected.size === 0}
              className="px-4 py-2.5 rounded-lg bg-accent text-white hover:bg-interactive-hover transition-colors text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting && (
                <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" />
              )}
              {selected.size > 0
                ? `Send ${selected.size} invitation${selected.size === 1 ? '' : 's'}`
                : 'Send invitations'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default InvitePlayersModal;
